import React, { useEffect, useState } from 'react';
import { Box, Button, Typography, IconButton, Paper } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';

const InstallPWA: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Check if iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    setIsIOS(isIOSDevice);

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return;
    }

    // Handle PWA install prompt
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Show iOS install instructions if not installed
    if (isIOSDevice && !window.matchMedia('(display-mode: standalone)').matches) {
      // Only show after user has used the app for a bit
      setTimeout(() => {
        const hasSeenIOSPrompt = localStorage.getItem('hasSeenIOSInstallPrompt');
        if (!hasSeenIOSPrompt) {
          setShowBanner(true);
        }
      }, 30000); // Show after 30 seconds
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
    }

    setDeferredPrompt(null);
    setShowBanner(false);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    if (isIOS) {
      localStorage.setItem('hasSeenIOSInstallPrompt', 'true');
    }
  };

  if (!showBanner) return null;

  return (
    <Paper
      elevation={3}
      sx={{
        position: 'fixed',
        bottom: 20,
        left: 20,
        right: 20,
        p: 2,
        zIndex: 1300,
        maxWidth: 400,
        mx: 'auto',
        bgcolor: 'background.paper',
      }}
    >
      <Box display="flex" alignItems="start" gap={2}>
        <DownloadIcon sx={{ width: 24, height: 24, color: 'primary.main', flexShrink: 0, mt: 0.5 }} />
        <Box flex={1}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Install Botala App
          </Typography>
          {isIOS ? (
            <Typography variant="body2" color="text.secondary">
              Tap the share button <span style={{ fontFamily: 'SF Pro Display' }}>􀈂</span> and select "Add to Home Screen"
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Install the app for offline access and better experience
            </Typography>
          )}
        </Box>
        <IconButton size="small" onClick={handleDismiss}>
          <CloseIcon sx={{ width: 16, height: 16 }} />
        </IconButton>
      </Box>
      
      {!isIOS && (
        <Box display="flex" gap={1} mt={2}>
          <Button
            variant="contained"
            size="small"
            fullWidth
            onClick={handleInstall}
            startIcon={<DownloadIcon sx={{ width: 16, height: 16 }} />}
          >
            Install
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={handleDismiss}
          >
            Later
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default InstallPWA;